import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useState } from "react";
import { BadgeCheck, Clock, TrendingUp, Sprout, Check, Plus, Heart, Lock, Sparkles, Trophy } from "lucide-react";
import { useCompare } from "@/context/CompareContext";
import { useAuth, authAxios } from "@/context/AuthContext";
import { SignedUpModal, AiPostModal } from "@/components/ProgramActionsModal";

export const ProgramCard = ({ program }) => {
  const { toggle, has } = useCompare();
  const { user, refreshUser } = useAuth();
  const inCompare = has(program.slug);
  const isFav = !!(user && user.favorites?.includes(program.slug));
  const isTeaser = program.is_teaser || program.viewer_tier === "anon";
  const isLocked = program.locked || program.is_exclusive;
  const showExclusiveBadge = program.is_exclusive;
  const isPaid = !!user?.is_paid;
  const [showSignedUp, setShowSignedUp] = useState(false);
  const [showAiPost, setShowAiPost] = useState(false);

  const toggleFavorite = async () => {
    try {
      if (isFav) {
        await authAxios.delete(`/user/favorites/${program.slug}`);
        toast.success(`${program.name} removed from saved`);
      } else {
        await authAxios.post(`/user/favorites/${program.slug}`);
        toast.success(`${program.name} saved to your dashboard`);
      }
      refreshUser();
    } catch (err) {
      console.error("favorites toggle failed:", err);
      toast.error("Could not update favorites");
    }
  };

  return (
    <div data-testid={`program-card-${program.slug}`} className={`card-lift bg-white border rounded-lg p-6 flex flex-col h-full relative ${showExclusiveBadge ? "border-amber-300 shadow-amber-100 shadow-md" : "border-slate-200"}`}>
      {showExclusiveBadge && (
        <div data-testid={`exclusive-badge-${program.slug}`} className="absolute -top-3 left-6 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold uppercase tracking-wider shadow">
          <Sparkles className="w-3 h-3" /> Members Only
        </div>
      )}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-heading font-semibold text-lg tracking-tight leading-snug">{program.name}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{program.company}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {program.editors_choice && (
            <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
              <BadgeCheck className="w-3 h-3" /> Editor's Pick
            </span>
          )}
          {program.beginner_friendly && (
            <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
              <Sprout className="w-3 h-3" /> Beginner
            </span>
          )}
        </div>
      </div>
      <span className="inline-block self-start text-xs font-medium text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full mb-3">{program.niche_name}</span>
      <p className="text-sm text-slate-600 leading-relaxed line-clamp-2 mb-4">{program.description}</p>
      <div className="grid grid-cols-3 gap-2 border-t border-slate-100 pt-4 mb-4 mt-auto">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Commission</p>
          <p className="text-sm font-semibold text-emerald-700 leading-tight">{program.commission}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><Clock className="w-3 h-3" />Cookie</p>
          <p className="text-sm font-semibold">{program.cookie_days}d</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><TrendingUp className="w-3 h-3" />EPC</p>
          <p className="text-sm font-semibold">${(program.epc || 0).toFixed(2)}</p>
        </div>
      </div>

      {isTeaser ? (
        <div className="flex flex-col gap-2">
          <Link
            to="/signup"
            data-testid={`teaser-cta-${program.slug}`}
            className="text-center text-sm font-bold py-2.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-500 transition-colors"
          >
            Sign up free to unlock →
          </Link>
          <p className="text-[11px] text-slate-400 text-center">Real signup link revealed after email verification</p>
        </div>
      ) : isLocked ? (
        <div className="flex flex-col gap-2">
          <Link
            to="/membership"
            data-testid={`locked-cta-${program.slug}`}
            className="text-center text-sm font-bold py-2.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2"
          >
            <Lock className="w-4 h-4" /> Unlock for $1.99/mo
          </Link>
          <p className="text-[11px] text-slate-400 text-center">Exclusive Members Club program</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Link
              to={`/programs/${program.slug}`}
              data-testid={`view-details-${program.slug}`}
              className="flex-1 text-center text-sm font-medium py-2 rounded-full bg-slate-900 text-white hover:bg-slate-700 transition-colors duration-200"
            >
              View Details
            </Link>
            <button
              onClick={() => toggle(program)}
              data-testid={`compare-toggle-${program.slug}`}
              aria-label={inCompare ? "Remove from comparison" : "Add to comparison"}
              className={`px-3 py-2 rounded-full border text-sm font-medium transition-colors duration-200 flex items-center gap-1 ${
                inCompare ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 text-slate-600 hover:border-emerald-600 hover:text-emerald-700"
              }`}
            >
              {inCompare ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </button>
            {user && (
              <button
                onClick={toggleFavorite}
                data-testid={`favorite-toggle-${program.slug}`}
                aria-label={isFav ? "Remove from saved" : "Save program"}
                className={`px-3 py-2 rounded-full border text-sm font-medium transition-colors duration-200 ${
                  isFav ? "bg-rose-500 border-rose-500 text-white" : "border-slate-300 text-slate-600 hover:border-rose-400 hover:text-rose-500"
                }`}
              >
                <Heart className={`w-4 h-4 ${isFav ? "fill-current" : ""}`} />
              </button>
            )}
          </div>
          {user && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowSignedUp(true)}
                data-testid={`signed-up-${program.slug}`}
                className="flex-1 text-xs font-semibold py-2 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center justify-center gap-1.5"
              >
                <Trophy className="w-3.5 h-3.5" /> I signed up
              </button>
              <button
                onClick={() => setShowAiPost(true)}
                data-testid={`ai-post-${program.slug}`}
                className="flex-1 text-xs font-semibold py-2 rounded-full border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 hover:from-amber-100 hover:to-orange-100 inline-flex items-center justify-center gap-1.5"
              >
                <Sparkles className="w-3.5 h-3.5" /> AI post
                {!isPaid && <Lock className="w-3 h-3 opacity-60" />}
              </button>
            </div>
          )}
        </div>
      )}
      <SignedUpModal open={showSignedUp} onClose={() => setShowSignedUp(false)} program={program} />
      <AiPostModal open={showAiPost} onClose={() => setShowAiPost(false)} program={program} isPaid={isPaid} />
    </div>
  );
};
